import React, { FC, useEffect, useState } from 'react';
import { FieldProps } from 'formik';
import { Loader } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useIntl } from 'react-intl';
import { useAttachmentGroupWorkspace } from '../../services/hooks';
import { Loadable } from '../../types';
import { FileGroup, FileSelect } from './FileDrop';
import { InputError } from '../Atoms';

type FormAttachmentGroupProps = FieldProps & {
  buttonText?: string;
  group: string;
  onlyFileList?: boolean;
  onChangeActiveFile?: (file: Loadable<File> | null) => void;
};

export const FormAttachmentGroup: FC<FormAttachmentGroupProps> = ({
  buttonText,
  form,
  field,
  group,
  onlyFileList,
  onChangeActiveFile,
}) => {
  const {
    activeFile,
    activeFileOption,
    availableFiles,
    changes,
    handleFileSelected,
    handleAddNewFiles,
    handleRemoveActiveFile,
  } = useAttachmentGroupWorkspace(group, field.value);

  const [prevActiveFile, setPrevActiveFile] = useState<Loadable<File> | null>();

  const intl = useIntl();

  const meta = form.getFieldMeta(field.name);

  useEffect(() => {
    if (field.value !== changes) {
      form.setFieldValue(field.name, changes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changes]);

  useEffect(() => {
    if (activeFile !== prevActiveFile) {
      setPrevActiveFile(activeFile);
      onChangeActiveFile?.(activeFile);
    }
  }, [activeFile?.result]);

  return (
    <div>
      {activeFile?.loading ? (
        <Loader />
      ) : (
        <>
          {onlyFileList ? (
            <FileSelect
              availableFiles={availableFiles}
              selectedFileOption={activeFileOption}
              onSelectFile={handleFileSelected}
              onNewFilesAdded={handleAddNewFiles}
              onDeleteFile={handleRemoveActiveFile}
              showWhenEmpty
              buttonText={buttonText}
            />
          ) : (
            <FileGroup
              selectedFile={activeFile}
              selectedFileOption={activeFileOption}
              availableFiles={availableFiles}
              onSelectFile={handleFileSelected}
              onNewFilesAdded={handleAddNewFiles}
              onDeleteSelectedFile={handleRemoveActiveFile}
            />
          )}
        </>
      )}
      {activeFile?.error && (
        <ErrorMessage
          title={intl.formatMessage({ id: 'general.error.failedtoloadattachment' })}
          error={activeFile.error}
        />
      )}
      {meta.error && <InputError>{meta.error}</InputError>}
    </div>
  );
};
